import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, switchMap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import {
  BaseOneResponseInterface,
  BulkResponseDataInterface,
} from '../../../../shared/model/interface/crud-response-interface.model';
import * as oeeAppReducer from '../../../oee.reducer';
import * as AppActions from '../../../app/actions';
import { ErrorMessageService } from '../../../../shared/service/error-message.service';
import { IChangeActivity } from '../../../../view/settings/api-automation-settings/change-activity/change-activity.model';
import { ChangeActivityService } from './change-activity.service';
import {
  ActionTypes,
  ChangeActivitiesLoading,
  ChangeActivitiesLoaded,
  FetchError,
  GetChangeActivity,
  GetChangeActivityCompleted,
  EditChangeActivity,
  EditChangeActivityCompleted,
  AddChangeActivity,
  AddChangeActivityCompleted,
  DeleteChangeActivities,
  DeleteChangeActivitiesCompleted,
  ActivitiesDataLoading,
  ActivitiesDataCompleted,
  TasksDataLoading,
  TasksDataCompleted,
  ExtendedTasksDataLoading,
  ExtendedTasksDataCompleted,
  CleanerRulesDataCompleted,
  TaskLookupDataLoading,
  TaskLookupDataCompleted,
} from './change-activity.actions';
import { ActivityService } from '../../../../shared/service/filter/activity.service';
import { ApiAutomationLookupService } from '../shared/api-automation-lookup.service';
import { EApiAutomationResource } from '../shared/api-automation.model';
import { TaskService } from '../../../../shared/service/filter/task.service';

@Injectable()
export class ChangeActivityEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: ChangeActivityService,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
    private readonly errorMessageService: ErrorMessageService,
    private readonly activityService: ActivityService,
    private readonly tasksService: TaskService,
    private readonly lookupService: ApiAutomationLookupService,
  ) {}

  getDataForDatatable = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.CHANGE_ACTIVITYS_DATA_LOADING),
      switchMap((payload: ChangeActivitiesLoading) => {
        const andConditions = [];
        const searchText: string = payload.params.searchText?.trim() ?? '';
        let httpParams: HttpParams = new HttpParams()
          .append('sort', 'id,DESC')
          .append('limit', String(payload.params.perPage || 1000))
          .append('page', String(payload.params.page));

        if (searchText) {
          andConditions.push({
            $or: [{ name: { $cont: searchText } }, { extendedCleanerRuleIds: { $cont: searchText } }],
          });
        }

        if (andConditions.length > 0) {
          httpParams = httpParams.set('s', JSON.stringify({ $and: andConditions }));
        }

        if (payload.params.orderBy && payload.params.orderDesc !== undefined) {
          httpParams = httpParams.set('sort', `${payload.params.orderBy},${payload.params.orderDesc}`);
        }

        return this.service.getData(httpParams).pipe(
          switchMap((response) => of(new ChangeActivitiesLoaded(response))),
          catchError((error) => of(new FetchError(error))),
        );
      }),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.GET_CHANGE_ACTIVITY),
      switchMap((objectData: GetChangeActivity) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getOne(objectData.recordId).pipe(
          switchMap((response: BaseOneResponseInterface<IChangeActivity>) =>
            of(new GetChangeActivityCompleted(response), new AppActions.HideLoader()),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  addOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.ADD_CHANGE_ACTIVITY),
      switchMap((objectData: AddChangeActivity) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.addOne(objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<IChangeActivity>) =>
            of(
              new AddChangeActivityCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  editOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.EDIT_CHANGE_ACTIVITY),
      switchMap((objectData: EditChangeActivity) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.editOne(objectData.recordId, objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<IChangeActivity>) =>
            of(
              new EditChangeActivityCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  deleteOnes = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.DELETE_CHANGE_ACTIVITYS),
      switchMap((objectData: DeleteChangeActivities) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.deleteOnes(objectData.payload).pipe(
          switchMap((response: BulkResponseDataInterface) => {
            this.errorMessageService.getTranslatedErrorMessage(response.data);

            return of(
              new DeleteChangeActivitiesCompleted(objectData.payload.length > 1, response),
              new AppActions.HideLoader(),
            );
          }),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  getActivitiesData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.ACTIVITIES_DATA_LOADING),
      switchMap((objectData: ActivitiesDataLoading) =>
        from(
          this.activityService.getData(new HttpParams().set('s', JSON.stringify({ active: 1 })).set('limit', '1000')),
        ).pipe(
          switchMap((response) => of(new ActivitiesDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getTasksData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.TASKS_DATA_LOADING),
      switchMap((objectData: TasksDataLoading) =>
        from(
          this.tasksService.getData(
            new HttpParams()
              .set('s', JSON.stringify({ statusId: 1, activityId: objectData.activityId }))
              .set('limit', '1000')
              .set('join', 'line||title'),
          ),
        ).pipe(
          switchMap((response) => of(new TasksDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getExtendedTasksData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.EXTENDED_TASKS_DATA_LOADING),
      switchMap((objectData: ExtendedTasksDataLoading) =>
        from(
          this.tasksService.getData(
            new HttpParams()
              .set('s', JSON.stringify({ statusId: 1, activityId: objectData.activityId }))
              .set('limit', '1000')
              .set('join', 'line||title'),
          ),
        ).pipe(
          switchMap((response) => of(new ExtendedTasksDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getCleanerRulesData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.CLEANER_RULES_DATA_LOADING),
      switchMap(() =>
        this.lookupService.getOptions(EApiAutomationResource.changeActivity).pipe(
          switchMap((response) => of(new CleanerRulesDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getTaskLookupData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.TASK_LOOKUP_DATA_LOADING),
      switchMap((objectData: TaskLookupDataLoading) =>
        this.lookupService.getTaskOptions(objectData.taskIds).pipe(
          switchMap((response) => of(new TaskLookupDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );
}
